<template>
    <div class="d-flex flex-columns">
        <v-autocomplete
            class="tag-select"
            dense
            outlined
            v-bind="$attrs"
            :items="items"
            :filter="filter"
            :search-input.sync="searchInput"
            chips
            deletable-chips
            small-chips
            :menu-props="{ contentClass: 'tag-select-menu' }"
            :item-text="itemText"
            :item-value="itemValue"
            multiple
            :value="value"
            v-on="$listeners"
            @change="onChange"
        >
            <template #item="data">
                <v-list-item-content>
                    <v-list-item-title v-html="data.item.tag"></v-list-item-title>
                </v-list-item-content>
            </template>
        </v-autocomplete>
        <IconBtn
            v-if="showAdd"
            margin-class="mt-1 mb-1 mr-0 ml-0"
            color="transparent"
            icon-cls="new"
            icon-color="black"
            icon-size="20"
            :tooltip="$t('tag_erstellen')"
            @click="onAddTagBtn"
        />
        <IconBtn
            v-if="showDownload"
            margin-class="mt-1 mb-1 mr-0 ml-0"
            color="transparent"
            :href="tagDownloadUrl()"
            target="_blank"
            icon-cls="download"
            icon-color="black"
            icon-size="20"
            :tooltip="$t('download')"
        />
        <v-dialog v-model="showAddDlg" max-width="500px">
            <v-card>
                <v-card-title>
                    <span class="text-h5">{{ $t('tag_erstellen') }}</span>
                </v-card-title>
                <v-card-text>
                    <tag-group-select v-model="tagGroup" :label="$t('tag_group')"></tag-group-select>
                    <v-text-field v-model="newTag" autofocus :label="$t('tag_ex')" dense outlined />
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="cancelBtn">{{ $t('cancel') }}</v-btn>
                    <v-btn color="action" text :loading="saving" @click="saveBtn">{{ $t('save') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { tagDownloadUrl } from '@/helpers/globals';
import TagGroupSelect from '@/components/tag_group/select.vue';
import { errToStr } from '@/helpers/tools';
import { Tag } from '@/models/Tag';

export default {
    name: 'TagSelect',

    components: {
        TagGroupSelect,
    },

    props: {
        value: {
            type: Array,
            default: () => [],
        },
        forMagazin: {
            type: Boolean,
            default: false,
        },
        forEvent: {
            type: Boolean,
            default: false,
        },
        forAddress: {
            type: Boolean,
            default: false,
        },
        forSchedule: {
            type: Boolean,
            default: false,
        },
        isForm: {
            type: Boolean,
            default: false,
        },
        isOccasion: {
            type: Boolean,
            default: false,
        },
        isAdditionalInfo: {
            type: Boolean,
            default: false,
        },
        showDownload: {
            type: Boolean,
            default: true,
        },
        showAdd: {
            type: Boolean,
            default: false,
        },
        itemText: {
            type: String,
            default: 'tag',
        },
        itemValue: {
            type: String,
            default: 'id',
        },
    },

    data() {
        return {
            saving: false,
            showAddDlg: false,
            newTag: '',
            tagGroup: null,
            searchInput: '',
        };
    },

    computed: {
        ...mapState('tag', {
            loaded: 'loaded',
            collection: 'collection',
        }),

        items() {
            if (this.collection) {
                // we need to insert item group headers -
                // for that, we assume that all tags are ordered by their tag group first
                // (see store/tag/load action). We generate an item array with injected group
                // headers.
                const items = [];
                let actGroup = null;
                this.collection.each((item) => {
                    // first, we check if we have to process the item at all:
                    let selectItem = false;
                    if (this.forMagazin && item.for_magazin) {
                        selectItem = true;
                    }
                    if (this.forEvent && item.for_event) {
                        selectItem = true;
                    }
                    if (this.forAddress && item.for_address) {
                        selectItem = true;
                    }
                    if (this.forSchedule && item.for_schedule) {
                        selectItem = true;
                    }
                    if (this.isForm && item.is_form) {
                        selectItem = true;
                    }
                    if (this.isOccasion && item.is_occasion) {
                        selectItem = true;
                    }
                    if (this.isAdditionalInfo && item.is_additional_info) {
                        selectItem = true;
                    }
                    // the tag should not be used, return:
                    if (!selectItem) {
                        return true;
                    }
                    // do we need to insert a group header?
                    if (item.tag_group && actGroup !== item.tag_group) {
                        actGroup = item.tag_group;
                        items.push({ header: item.tag_group });
                    }
                    items.push(item);
                });
                return items;
            } else {
                return [];
            }
        },
    },

    mounted() {
        this.loadData();
    },

    methods: {
        ...mapActions('tag', {
            loadData: 'load',
        }),
        filter(item, queryText, itemText) {
            let title = (item.tag || '').toLowerCase();
            let group = (item.tag_group || '').toLowerCase();
            queryText = queryText.toLowerCase();
            return title.indexOf(queryText) >= 0 || group.indexOf(queryText) >= 0;
        },
        reload() {
            this.loadData({ force: true });
        },
        remove(item) {
            this.$emit('input', this.value ? this.value.filter((i) => i !== item[this.itemValue]) : []);
        },
        tagDownloadUrl() {
            const opts = {};
            if (this.forMagazin) {
                opts.for_magazin = true;
            }
            if (this.forEvent) {
                opts.for_event = true;
            }
            if (this.forAddress) {
                opts.for_address = true;
            }
            if (this.forSchedule) {
                opts.for_schedule = true;
            }
            return tagDownloadUrl(opts);
        },

        onAddTagBtn() {
            this.newTag = '';
            this.showAddDlg = true;
        },

        cancelBtn() {
            this.newTag = '';
            this.showAddDlg = false;
        },

        async saveBtn() {
            let groupId = this.tagGroup;
            let tag = this.newTag.trim();
            let opts = {
                tag_group_id: groupId,
                tag: tag,
                for_magazin: this.forMagazin,
                for_event: this.forEvent,
                for_address: this.forAddress,
            };
            let model = new Tag(opts);
            this.saving = true;
            try {
                await model.save();
                this.showAddDlg = false;
                this.reload();
                this.$emit('tag-added', model);
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            } finally {
                this.saving = false;
            }
        },

        onChange() {
            this.searchInput = '';
        },
    },
};
</script>

<style lang="scss">
.tag-select-menu {
    .v-list-item {
        min-height: 25px !important;
        .v-list-item__content {
            padding: 0 !important;
        }
    }
}
</style>
